import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { CATEGORY_LABELS, ServiceCategory } from '@/constants/serviceCategories';
import { getServiceStatusBadge } from '@/utils/badgeUtils';
import { formatDate } from '@/utils/dateUtils';
import { getErrorMessage } from '@/utils/errorUtils';
import { MyService, serviceRegistrationAPI } from '@/services/apis/serviceRegistrationAPI';
import ServiceRegisterDialog from './ServiceRegisterDialog';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';

/**
 * 내 서비스 — 내가 올린 것과 그 상태 (#136)
 *
 * 사용자가 자기 서비스를 올리고 상태를 본다. 승인은 운영자가 한다.
 * 등록대기·거부된 신청은 스스로 내릴 수 있고, 승인된 것은 이미 쓰는 사람이 있을 수 있어
 * 운영자만 내린다.
 */

/** 신청자가 스스로 내릴 수 있는 상태. 서버도 같은 기준으로 막는다 */
const WITHDRAWABLE = ['pending', 'rejected'];

export const MyServicesTab: React.FC = () => {
  const [services, setServices] = useState<MyService[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [registerOpen, setRegisterOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setServices(await serviceRegistrationAPI.listMine());
    } catch (e) {
      setError(getErrorMessage(e, '내 서비스를 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleWithdraw = async (service: MyService) => {
    if (!window.confirm(`'${service.service_name}' 신청을 취소할까요?`)) return;
    setError('');
    setSuccess('');
    try {
      const { message } = await serviceRegistrationAPI.withdraw(service.service_slug);
      setSuccess(message);
      await load();
    } catch (e) {
      setError(getErrorMessage(e, '신청을 취소하지 못했습니다.'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div>
          <h3 className="text-base font-semibold">내 서비스</h3>
          <p className="text-sm text-gray-500">
            내가 올린 서비스와 그 상태입니다. 승인은 운영자가 합니다.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} className="ml-auto">
          <RefreshCw className="w-4 h-4 mr-1" />
          새로고침
        </Button>
        <Button size="sm" onClick={() => setRegisterOpen(true)}>
          <Plus className="w-4 h-4 mr-1" />
          서비스 등록
        </Button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
          {success}
        </div>
      )}

      {!loading && services.length === 0 && (
        <div className="border rounded-md p-8 text-center text-sm text-gray-500">
          아직 올린 서비스가 없습니다. <strong>서비스 등록</strong>으로 신청하세요.
        </div>
      )}

      {services.length > 0 && (
        <div className="space-y-2">
          {services.map((service) => {
            return (
              <div
                key={service.service_slug}
                className="border rounded-md p-4 flex items-start gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{service.service_name}</span>
                    {getServiceStatusBadge(service.status)}
                    {service.is_public && (
                      <Badge variant="success" size="sm">
                        공개
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 font-mono">{service.service_slug}</p>
                  {service.service_description && (
                    <p className="text-sm text-gray-600 mt-1">{service.service_description}</p>
                  )}
                  <div className="text-xs text-gray-500 mt-2 flex flex-wrap gap-x-4 gap-y-1">
                    {service.service_url && (
                      <a
                        href={service.service_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 underline"
                      >
                        {service.service_url}
                      </a>
                    )}
                    {service.category && (
                      <span>
                        {CATEGORY_LABELS[service.category as ServiceCategory] || service.category}
                      </span>
                    )}
                    <span>신청 {formatDate(service.created_at)}</span>
                    {service.approved_at && <span>승인 {formatDate(service.approved_at)}</span>}
                  </div>
                </div>

                {WITHDRAWABLE.includes(service.status) ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleWithdraw(service)}
                    title="신청 취소"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                ) : (
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    내리려면 운영자에게
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ServiceRegisterDialog
        open={registerOpen}
        role="user"
        onClose={() => setRegisterOpen(false)}
        onRegistered={load}
      />
    </div>
  );
};

export default MyServicesTab;
